import { useId } from "react";
import { parseStripeReturn } from "@froppio/host-auth";
import { Link } from "react-router-dom";
import froppioBusiness from "@/assets/froppio-business.svg";
import { navigationState } from "@/core/routing/navigationPolicy";
import { Button } from "@/shared/components/shadcn/Button";
import { Card } from "@/shared/components/ui/Card";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { SectionHeader } from "@/shared/components/ui/SectionHeader";
import { StatusBadge, type StatusBadgeProps } from "@/shared/components/ui/StatusBadge";

const outcomes = {
  returned: {
    label: "Back from Stripe",
    title: "Finish linking your Stripe account",
    description: "Check your status to see if your Stripe account is linked or if Stripe needs more information.",
    tone: "neutral",
  },
  expired: {
    label: "Link expired",
    title: "Your Stripe link expired",
    description: "Check your status to continue linking your Stripe account.",
    tone: "pending",
  },
  error: {
    label: "Not linked",
    title: "Your Stripe account wasn't linked",
    description: "Something went wrong while linking your account. Check your status and try again.",
    tone: "danger",
  },
} satisfies Record<"returned" | "expired" | "error", { label: string; title: string; description: string; tone: StatusBadgeProps["tone"] }>;

export function StripeReturnPage() {
  const headingId = useId();
  const result = parseStripeReturn(window.location.href, window.location.origin, !import.meta.env.DEV);
  const outcome = result ? outcomes[result.outcome] : undefined;

  return (
    <Page>
      <PageHeader title="Stripe" />
      <PageContent>
        {!result || !outcome ? (
          <ErrorState message="This Stripe return link is invalid. Reopen Froppio and check Stripe settings." />
        ) : (
          <Card tone="matcha" role="region" aria-labelledby={headingId}>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <span className="text-[26px] font-extrabold tracking-tight text-primary">stripe</span>
              <StatusBadge tone={outcome.tone} className="bg-card/80">{outcome.label}</StatusBadge>
            </div>
            <div className="grid items-center gap-3 xs:grid-cols-[minmax(0,1fr)_90px] sm:grid-cols-[minmax(0,1fr)_150px] sm:gap-7">
              <div className="min-w-0 space-y-3 sm:max-w-[480px]">
                <SectionHeader title={outcome.title} headingId={headingId} />
                <p role="status" className="text-sm leading-relaxed">{outcome.description}</p>
                {result.channel === "native" ? (
                  <div className="space-y-2 pt-2">
                    <p className="text-sm font-bold leading-relaxed">Go back to the Froppio app to see your status.</p>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      If the app didn't open, open Froppio and go to More → Payments → Stripe setup.
                    </p>
                  </div>
                ) : (
                  <div className="pt-2">
                    <Button asChild>
                      <Link to={`/b/${result.businessId}/l/${result.locationId}/stripe`} state={navigationState("start")}>
                        Check Stripe status
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
              <img src={froppioBusiness} alt="" width={280} height={240} className="hidden h-auto w-full xs:block" />
            </div>
          </Card>
        )}
      </PageContent>
    </Page>
  );
}
